import clsx from "clsx";
import { useState } from "react";
import { ExclamationTriangleFill, PencilFill, X } from "react-bootstrap-icons";
import type { Interrogatory, ViewCommunicationItem } from "@/briefpoint-client";
import { COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW } from "@/lib/constants";
import Button from "../Button";
import styles from "./ReviewClientTaskBox.module.scss";
export default function ReviewClientTaskBox({
  communication,
  interrogatories,
  setDeleteCommunication,
  setEditCommunication,
}: {
  communication: ViewCommunicationItem;
  interrogatories: Interrogatory[];
  setDeleteCommunication: (communication: ViewCommunicationItem) => void;
  setEditCommunication: (communication: ViewCommunicationItem) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  function findInterrogatory(interrogatoryId: string) {
    return (
      interrogatories?.find(
        (interrogatory) => interrogatory.id.toString() === interrogatoryId,
      ) ?? null
    );
  }

  const unassigned = !communication.responseInfo?.associatedObjectIds?.length;

  return (
    <div className={styles.communicationContainer}>
      <div className={styles.clientTaskContainer}>
        <div
          className={clsx(
            styles.communicationTitle,
            unassigned && styles.unassignedTask,
          )}
        >
          <div className="fw-bold">Client Task</div>
          {unassigned && (
            <div className="d-flex gap-2 align-items-center">
              <ExclamationTriangleFill
                size={16}
                style={{ color: "var(--feedback-warning-dark)" }}
              />
              <span className="fs-7">Not visible to your client</span>
            </div>
          )}
        </div>
        <div className={styles.communicationContent}>
          <div className={styles.communicationContentText}>
            {communication.communication}
          </div>
          <div className={styles.communicationAction}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteCommunication(communication)}
              className={styles.communicationActionButton}
            >
              <X size={12} style={{ scale: 2 }} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditCommunication(communication)}
              className={styles.communicationActionButton}
            >
              <PencilFill size={14} />
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.communicationAssociatedContainer}>
        <div className={styles.curveGraphicContainer}>
          {/* add animation to the curve graphic */}
          {communication.responseInfo?.associatedObjectIds
            ?.slice(
              0,
              isExpanded ? undefined : COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW,
            )
            .map((_, index) => (
              <div
                key={index}
                className={styles.curveGraphic}
                style={{ top: index * 59 }}
              />
            ))}
        </div>
        {(communication.responseInfo?.associatedObjectIds?.length || 0) > 0 && (
          <div className={styles.clientUploadsContainer}>
            <div className={styles.clientUploadsTitle}>
              Client uploads will be applied to:
            </div>

            {communication.responseInfo?.associatedObjectIds
              ?.sort((a, b) => Number(a) - Number(b))
              .slice(
                0,
                isExpanded ? undefined : COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW,
              )
              .map((objectId, index) => (
                <div className={styles.clientUploadsContent} key={index}>
                  <div className={styles.clientUploadsContentText}>
                    <b>No. {findInterrogatory(objectId)?.number}: </b>
                    {findInterrogatory(objectId)?.text}
                  </div>
                </div>
              ))}
            {(communication.responseInfo?.associatedObjectIds?.length ?? 0) >
              COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW && (
              <div className="d-flex gap-2 p-2 ps-3 align-items-center">
                {!isExpanded && (
                  <div className="fw-bold">
                    +{" "}
                    {(communication.responseInfo?.associatedObjectIds?.length ??
                      0) - COLLAPSED_ASSOCIATED_OBJECTS_TO_SHOW}{" "}
                    more
                  </div>
                )}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? "Show Less" : "Show All"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
